export const PRODUCT_STATUSES = ['ACTIVE', 'DRAFT', 'ARCHIVED'] as const;

export type ProductStatus = (typeof PRODUCT_STATUSES)[number];

/** Default report filter: active products only (matches existing out-of-stock behavior). */
export const DEFAULT_PRODUCT_STATUSES: ProductStatus[] = ['ACTIVE'];

/** Media list default: active + draft (hide archived product galleries). */
export const MEDIA_DEFAULT_PRODUCT_STATUSES: ProductStatus[] = [
  'ACTIVE',
  'DRAFT',
];

/**
 * Home + sales dashboard analytics: include all product statuses so revenue
 * tracks closer to Xero sales accounts (archived/draft products still sold).
 */
export const ANALYTICS_DEFAULT_PRODUCT_STATUSES: ProductStatus[] = [
  ...PRODUCT_STATUSES,
];

export function isProductStatus(value: string): value is ProductStatus {
  return (PRODUCT_STATUSES as readonly string[]).includes(value.toUpperCase());
}

export function normalizeProductStatuses(values: readonly string[]): ProductStatus[] {
  const normalized = values
    .map((s) => s.toUpperCase())
    .filter((s): s is ProductStatus => isProductStatus(s));
  return normalized.length > 0 ? normalized : [...DEFAULT_PRODUCT_STATUSES];
}

/** Button label for product status filter popovers. */
export function productStatusLabel(statuses: readonly ProductStatus[]): string {
  if (statuses.length === 0) return 'Product status';
  if (statuses.length === PRODUCT_STATUSES.length) return 'Product: All statuses';
  if (statuses.length === 1) return `Product: ${formatProductStatus(statuses[0]!)}`;
  return `Product: ${statuses.map((status) => formatProductStatus(status)).join(', ')}`;
}

/** "Active", "Draft"…; the empty dash ("—", the frontend's EMPTY) when there is none. */
export function formatProductStatus(status: string | null | undefined): string {
  if (!status) return '—';
  return status.charAt(0) + status.slice(1).toLowerCase();
}

/**
 * Products list: the Xero status of a Shopify variant that has no Xero item yet. New products'
 * SKUs reach Xero through Parex some time after they are created on Shopify.
 */
export const XERO_STATUS_NOT_IN_XERO = 'not_in_xero';

/** Compact pill: Draft → D, Archived → A, Active → none. */
export function productStatusLetter(
  status: string | null | undefined
): { letter: 'D' | 'A'; label: string } | null {
  const key = status?.trim().toUpperCase();
  if (key === 'DRAFT') return { letter: 'D', label: formatProductStatus('DRAFT') };
  if (key === 'ARCHIVED') return { letter: 'A', label: formatProductStatus('ARCHIVED') };
  return null;
}
